import { apiClient } from './apiClient';
import { persistTokens, refreshAccessToken, type TokenPair } from './authTokens';

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  role: 'admin' | 'sales' | 'employee';
  tenant: {
    id: string;
    name: string;
    domain: string;
    is_active: boolean;
    created_at: string;
  };
  is_active: boolean;
  is_superuser?: boolean;
  must_reset_password?: boolean;
  date_joined: string;
  email_verified_at?: string | null;
  trust_state?: 'normal' | 'trusted' | 'review' | 'restricted';
}

export interface AuthResponse {
  access: string;
  refresh: string;
  user: User;
  tenant: {
    id: string;
    name: string;
    domain: string;
    is_active: boolean;
    created_at: string;
  };
  message?: string;
}

export interface RefreshTokenResponse {
  access: string;
  refresh?: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  password_confirm: string;
  tenant_name: string;
  phone?: string;
  accept_terms: boolean;
  website?: string;
  turnstile_token?: string;
}

class AuthService {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    return apiClient.publicPost('/auth/login/', credentials);
  }

  async register(data: RegisterData): Promise<AuthResponse> {
    return apiClient.publicPost('/auth/register/', data);
  }

  async verifyEmail(token: string): Promise<{ message?: string; email_verified?: boolean }> {
    return apiClient.publicPost('/auth/verify-email/', { token });
  }

  async resendVerification(): Promise<{ message?: string }> {
    return apiClient.post('/auth/resend-verification/', {});
  }

  async refreshToken(refreshToken: string): Promise<RefreshTokenResponse> {
    const previousRefresh = localStorage.getItem('refreshToken');
    localStorage.setItem('refreshToken', refreshToken);
    const ok = await refreshAccessToken();
    if (!ok) {
      if (previousRefresh) {
        localStorage.setItem('refreshToken', previousRefresh);
      } else {
        localStorage.removeItem('refreshToken');
      }
      throw new Error('Unable to refresh session.');
    }
    const access = localStorage.getItem('accessToken');
    if (!access) {
      throw new Error('Unable to refresh session.');
    }
    return {
      access,
      refresh: localStorage.getItem('refreshToken') || undefined,
    };
  }

  async logout(refreshToken: string): Promise<void> {
    await apiClient.publicPost('/auth/logout/', { refresh: refreshToken });
  }

  async setPassword(newPassword: string, confirmPassword: string): Promise<void> {
    await apiClient.post('/auth/set-password/', { new_password: newPassword, confirm_password: confirmPassword });
  }

  async requestPasswordReset(email: string): Promise<void> {
    await apiClient.publicPost('/auth/password-reset/', { email });
  }

  async resetPassword(token: string, newPassword: string, confirmPassword: string): Promise<void> {
    await apiClient.publicPost('/auth/password-reset/confirm/', {
      token,
      new_password: newPassword,
      confirm_password: confirmPassword,
    });
  }

  async probeProfile(): Promise<User> {
    return apiClient.probe('/profile/');
  }

  async getProfile(): Promise<User> {
    return apiClient.get('/profile/');
  }

  storeTokens(tokens: TokenPair): void {
    persistTokens(tokens);
  }
}

export const authService = new AuthService();
